import React, { useEffect, useMemo, useState } from 'react';
import { saveKPIGoals } from '../../services/api';
import { fromInput, MONTHS, toInput, unitSuffix } from './kpiFormat';

const SCORES = [10, 9, 8, 7, 6, 5, 4, 3, 2, 1];

const draftFrom = (role) => ({
  metrics: role.metrics.map(m => ({
    key: m.key,
    weight: m.goal ? String(+(m.goal.weight * 100).toFixed(2)) : '',
    lower: m.goal ? m.goal.lower : !!m.lower,
    thresholds: SCORES.map((_, i) => toInput(m.unit, m.goal?.thresholds?.[i] ?? null)),
  })),
  kind: role.bonus.kind,
  tiers: role.bonus.tiers.map(t => ({
    min_score: String(t.min_score),
    amount: role.bonus.kind === 'percent' ? String(+(t.amount * 100).toFixed(3)) : String(t.amount),
  })),
});

const num = (s) => {
  const n = Number(String(s).replace(/\s/g, '').replace(',', '.'));
  return String(s).trim() === '' || !Number.isFinite(n) ? null : n;
};

/* Порядок порогов: для «больше — лучше» каждое следующее (на меньший балл)
   не больше предыдущего, для «меньше — лучше» — не меньше. */
const badOrder = (row) => {
  const v = row.thresholds.map(num);
  return v.map((x, i) => i > 0 && x !== null && v[i - 1] !== null
    && (row.lower ? x < v[i - 1] : x > v[i - 1]));
};

const KpiGoalsEditor = ({ data, role: roleKey, onRole, period, closed, run }) => {
  const role = data.roles.find(r => r.key === roleKey) || data.roles[0];
  const [draft, setDraft] = useState(() => draftFrom(role));
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);

  // Сервер вернул сохранённые цели — черновик становится ими.
  useEffect(() => { setDraft(draftFrom(role)); setErrors([]); }, [role]);

  const saved = useMemo(() => JSON.stringify(draftFrom(role)), [role]);
  const dirty = JSON.stringify(draft) !== saved;
  const weightSum = draft.metrics.reduce((s, m) => s + (num(m.weight) || 0), 0);
  const weightOk = Math.abs(weightSum - 100) < 0.5;

  const setMetric = (i, patch) => setDraft(d => ({
    ...d, metrics: d.metrics.map((m, j) => (j === i ? { ...m, ...patch } : m)),
  }));

  const setThreshold = (i, k, value) => setDraft(d => ({
    ...d,
    metrics: d.metrics.map((m, j) => (j === i
      ? { ...m, thresholds: m.thresholds.map((t, n) => (n === k ? value : t)) } : m)),
  }));

  /* Равномерная шкала между целью на 10 и порогом на 1 — так управляющий
     задаёт две цифры, а не десять. */
  const fillEven = (i) => {
    const row = draft.metrics[i];
    const a = num(row.thresholds[0]);
    const b = num(row.thresholds[9]);
    if (a === null || b === null) return;
    const step = (b - a) / 9;
    const decimals = Math.abs(step) < 1 ? 2 : 0;
    setMetric(i, {
      thresholds: SCORES.map((_, k) => String(+(a + step * k).toFixed(decimals))),
    });
  };

  const setTier = (i, patch) => setDraft(d => ({
    ...d, tiers: d.tiers.map((t, j) => (j === i ? { ...t, ...patch } : t)),
  }));

  const save = async () => {
    const metrics = draft.metrics.map(m => {
      const def = role.metrics.find(x => x.key === m.key);
      return {
        key: m.key,
        weight: (num(m.weight) || 0) / 100,
        lower: m.lower,
        thresholds: m.thresholds.map(t => fromInput(def.unit, t) ?? null),
      };
    });
    const bonus = {
      kind: draft.kind,
      tiers: draft.tiers.map(t => ({
        min_score: num(t.min_score),
        amount: draft.kind === 'percent' ? (num(t.amount) || 0) / 100 : num(t.amount),
      })),
    };
    setSaving(true);
    setErrors([]);
    try {
      await run(() => saveKPIGoals(period.year, period.month, role.key, metrics, bonus),
        `Цели «${role.name}» на ${MONTHS[period.month - 1].toLowerCase()} сохранены`);
    } catch (e) {
      setErrors(e.response?.data?.errors || []);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="kpi2-goals">
      <div className="kpi2-chips" role="tablist" aria-label="Должность">
        {data.roles.map(r => (
          <button key={r.key} type="button" role="tab" aria-selected={r.key === role.key}
            className={`kpi2-chip ${r.key === role.key ? 'active' : ''} ${r.issues.length ? 'has-issue' : ''}`}
            onClick={() => onRole(r.key)}>
            {r.name}
          </button>
        ))}
      </div>

      <div className="kpi2-goals-layout">
        <div className="kpi2-goals-main">
          <section className="kpi2-card">
            <div className="kpi2-card-head">
              <h3>Цели «{role.name}» на {MONTHS[period.month - 1].toLowerCase()} {period.year}</h3>
              <p>
                {closed
                  ? 'Месяц закрыт — цели зафиксированы. Чтобы поменять, администратор открывает месяц заново.'
                  : 'Цели действуют только для этого месяца. Новый месяц начинается с копии целей прошлого.'}
              </p>
            </div>

            {draft.metrics.map((row, i) => {
              const def = role.metrics.find(x => x.key === row.key);
              const bad = badOrder(row);
              return (
                <div key={row.key} className="kpi2-goal">
                  <div className="kpi2-goal-head">
                    <div className="kpi2-goal-title">
                      <strong>{def.name}</strong>
                      <span className="kpi2-muted">{def.hint}</span>
                    </div>
                    <div className="kpi2-goal-opts">
                      <label className="kpi2-mini-field">
                        <span>Вес</span>
                        <span className="kpi2-input-wrap">
                          <input className="kpi2-input" inputMode="decimal" value={row.weight} disabled={closed}
                            onChange={e => setMetric(i, { weight: e.target.value })} aria-label={`Вес: ${def.name}`} />
                          <span className="kpi2-input-unit">%</span>
                        </span>
                      </label>
                      <label className="kpi2-mini-field">
                        <span>Что лучше</span>
                        <select value={row.lower ? 'lower' : 'higher'} disabled={closed}
                          onChange={e => setMetric(i, { lower: e.target.value === 'lower' })}>
                          <option value="higher">больше</option>
                          <option value="lower">меньше</option>
                        </select>
                      </label>
                    </div>
                  </div>

                  <div className="kpi2-goal-scale">
                    {SCORES.map((s, k) => (
                      <label key={s} className={`kpi2-goal-cell s${s} ${bad[k] ? 'is-bad' : ''}`}>
                        <span className="kpi2-goal-score">{s} {s === 10 ? '· цель' : s === 7 ? '· премия' : ''}</span>
                        <input className="kpi2-input" inputMode="decimal" value={row.thresholds[k]} disabled={closed}
                          onChange={e => setThreshold(i, k, e.target.value)}
                          aria-label={`${def.name}: порог на ${s} баллов`} aria-invalid={bad[k] || undefined} />
                      </label>
                    ))}
                    <span className="kpi2-goal-unit">{unitSuffix(def.unit)}</span>
                  </div>
                  {!closed && (
                    <div className="kpi2-goal-foot">
                      <button type="button" className="kpi2-link" onClick={() => fillEven(i)}>
                        Заполнить шкалу равномерно между «10» и «1»
                      </button>
                      {bad.some(Boolean) && (
                        <span className="ui-text-danger">
                          Пороги не по порядку: при «{row.lower ? 'меньше' : 'больше'} — лучше» на 10 баллов
                          должно быть {row.lower ? 'самое маленькое' : 'самое большое'} значение.
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            <div className={`kpi2-weight-sum ${weightOk ? 'is-ok' : 'is-bad'}`}>
              <span>Сумма весов: <strong>{Math.round(weightSum * 100) / 100}%</strong></span>
              <div className="kpi2-weight-bar"><span style={{ width: `${Math.min(weightSum, 100)}%` }} /></div>
              <span>{weightOk ? 'всё верно' : 'должно быть ровно 100%'}</span>
            </div>
          </section>

          <section className="kpi2-card">
            <div className="kpi2-card-head">
              <h3>Шкала премии</h3>
              <p>Премия выплачивается по итоговому баллу: берётся самая высокая ступень, до которой дотянул сотрудник.</p>
            </div>
            <div className="kpi2-bonus-kind">
              <label>
                <input type="radio" name="kpi2-kind" checked={draft.kind === 'fixed'} disabled={closed}
                  onChange={() => setDraft(d => ({ ...d, kind: 'fixed' }))} />
                Фиксированная сумма, ₽
              </label>
              <label>
                <input type="radio" name="kpi2-kind" checked={draft.kind === 'percent'} disabled={closed}
                  onChange={() => setDraft(d => ({ ...d, kind: 'percent' }))} />
                Процент от личной выручки
              </label>
            </div>
            <div className="kpi2-tiers">
              {draft.tiers.map((t, i) => (
                <div key={i} className="kpi2-tier">
                  <label className="kpi2-mini-field">
                    <span>Балл от</span>
                    <input className="kpi2-input" inputMode="decimal" value={t.min_score} disabled={closed}
                      onChange={e => setTier(i, { min_score: e.target.value })} />
                  </label>
                  <label className="kpi2-mini-field">
                    <span>Премия</span>
                    <span className="kpi2-input-wrap">
                      <input className="kpi2-input" inputMode="decimal" value={t.amount} disabled={closed}
                        onChange={e => setTier(i, { amount: e.target.value })} />
                      <span className="kpi2-input-unit">{draft.kind === 'percent' ? '%' : '₽'}</span>
                    </span>
                  </label>
                  {!closed && draft.tiers.length > 1 && (
                    <button type="button" className="ui-icon-btn" aria-label="Убрать ступень"
                      onClick={() => setDraft(d => ({ ...d, tiers: d.tiers.filter((_, j) => j !== i) }))}>×</button>
                  )}
                </div>
              ))}
              {!closed && (
                <button type="button" className="kpi2-link"
                  onClick={() => setDraft(d => ({ ...d, tiers: [...d.tiers, { min_score: '', amount: '' }] }))}>
                  + ступень
                </button>
              )}
            </div>
          </section>

          {errors.length > 0 && (
            <div className="ui-inline-error">{errors.map(e => <div key={e}>{e}</div>)}</div>
          )}

          {!closed && (
            <div className="kpi2-savebar">
              <span className="kpi2-muted">{dirty ? 'Есть несохранённые изменения' : 'Изменений нет'}</span>
              <button type="button" className="ui-btn-ghost" disabled={!dirty || saving}
                onClick={() => { setDraft(draftFrom(role)); setErrors([]); }}>Отменить</button>
              <button type="button" className="ui-btn-primary" disabled={!dirty || saving} onClick={save}>
                {saving ? 'Сохраняем…' : 'Сохранить цели'}
              </button>
            </div>
          )}
        </div>

        <aside className="kpi2-guide">
          <h3>Как ставить цели</h3>
          <ol>
            <li><strong>Вес</strong> — насколько показатель важен. Сумма весов должности — 100%. Главному показателю дайте 30–40%.</li>
            <li><strong>Что лучше.</strong> Выручка и средний чек — «больше», списания, жалобы, недостачи — «меньше».</li>
            <li><strong>Порог на 10</strong> — хороший, но реальный результат. Удобно брать факт того же месяца прошлого года плюс 5–10%.</li>
            <li><strong>Порог на 7</strong> — минимум, с которого начинается премия. Ниже 7 итоговых баллов премии нет.</li>
            <li><strong>Порог на 1</strong> — провал. Ниже него показатель всё равно получает 1 балл.</li>
            <li>Заполните «10» и «1» и нажмите «Заполнить шкалу равномерно» — середина посчитается сама.</li>
          </ol>
          <div className="kpi2-guide-example">
            <strong>Пример.</strong> Средний чек: вес 40%, цель 1 500 ₽, порог 1 — 1 050 ₽. Официант набрал 1 420 ₽ —
            это между 1 400 (8 баллов) и 1 450 (9), значит 8 баллов, в итог идёт 0,4 × 8 = 3,2.
          </div>
        </aside>
      </div>
    </div>
  );
};

export default KpiGoalsEditor;
